import { Injectable, inject } from '@angular/core';
import { RUNTIME_CONFIG } from '../runtime-config';

/**
 * Accès direct à l'API Cognito Identity Provider, en `fetch`.
 *
 * Pourquoi pas une bibliothèque : les opérations utilisées ici sont des appels
 * JSON publics, non signés, sur un seul point d'entrée. `oidc-client-ts` ne sait
 * pas parler le flux par choix dont dépend le code par e-mail, et
 * `amazon-cognito-identity-js` — la seule à implémenter SRP dans un navigateur —
 * pas davantage. Les deux pèsent plus lourd que ce fichier.
 *
 * Conséquence assumée : le mot de passe est transmis à Cognito dans le tunnel
 * TLS au lieu d'être prouvé sans être transmis, comme le ferait SRP. C'est un
 * flux documenté et supporté par AWS, mais c'est un cran en dessous. Le pool
 * autorise toujours SRP : le jour où le code par e-mail cesserait d'être utile,
 * l'écran de connexion peut y revenir.
 */

/** Défis que Cognito peut réclamer, et méthodes de première authentification. */
export type ChallengeName =
  | 'PASSWORD'
  | 'PASSWORD_SRP'
  | 'EMAIL_OTP'
  | 'SMS_OTP'
  | 'WEB_AUTHN'
  | 'SELECT_CHALLENGE'
  | 'SMS_MFA'
  | 'SOFTWARE_TOKEN_MFA'
  | 'CUSTOM_CHALLENGE'
  | 'NEW_PASSWORD_REQUIRED';

export interface AuthTokens {
  readonly accessToken: string;
  readonly idToken: string;
  /** Absent lorsque Cognito renouvelle une session existante. */
  readonly refreshToken: string | undefined;
  /** Instant d'expiration en millisecondes depuis l'époque. */
  readonly expiresAt: number;
}

export interface PendingChallenge {
  readonly challenge: ChallengeName;
  readonly session: string;
}

export type AuthOutcome =
  | { readonly kind: 'tokens'; readonly tokens: AuthTokens }
  | { readonly kind: 'challenge'; readonly pending: PendingChallenge }
  /** Cognito laisse le choix de la méthode : c'est lui qui dicte la liste. */
  | {
      readonly kind: 'choice';
      readonly available: readonly ChallengeName[];
      readonly session: string;
    };

/**
 * Codes d'erreur Cognito effectivement rencontrés dans ces parcours. Ils sont
 * énumérés pour que les écrans puissent les traiter sans lire de message, dont
 * la formulation change au gré des versions et n'est pas traduite.
 */
export type CognitoErrorCode =
  | 'NotAuthorizedException'
  | 'UserNotFoundException'
  | 'UsernameExistsException'
  | 'CodeMismatchException'
  | 'ExpiredCodeException'
  | 'InvalidPasswordException'
  | 'InvalidParameterException'
  | 'LimitExceededException'
  | 'TooManyRequestsException'
  | 'TooManyFailedAttemptsException'
  | 'UserNotConfirmedException'
  | 'PasswordResetRequiredException'
  | 'UserLambdaValidationException'
  | 'unknown';

export class CognitoError extends Error {
  constructor(
    readonly code: CognitoErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'CognitoError';
  }
}

const KNOWN_CODES: readonly string[] = [
  'NotAuthorizedException',
  'UserNotFoundException',
  'UsernameExistsException',
  'CodeMismatchException',
  'ExpiredCodeException',
  'InvalidPasswordException',
  'InvalidParameterException',
  'LimitExceededException',
  'TooManyRequestsException',
  'TooManyFailedAttemptsException',
  'UserNotConfirmedException',
  'PasswordResetRequiredException',
  'UserLambdaValidationException',
];

/** `__type` arrive parfois préfixé par l'espace de noms : `…#NotAuthorizedException`. */
function parseErrorCode(raw: unknown): CognitoErrorCode {
  if (typeof raw !== 'string') {
    return 'unknown';
  }
  const name = raw.slice(raw.indexOf('#') + 1);
  return KNOWN_CODES.includes(name) ? (name as CognitoErrorCode) : 'unknown';
}

interface AuthResponse {
  readonly AuthenticationResult?: {
    readonly AccessToken?: string;
    readonly IdToken?: string;
    readonly RefreshToken?: string;
    readonly ExpiresIn?: number;
  };
  readonly ChallengeName?: string;
  readonly Session?: string;
  readonly AvailableChallenges?: string[];
}

/** Clé sous laquelle chaque défi attend sa réponse. */
const ANSWER_KEY: Partial<Record<ChallengeName, string>> = {
  EMAIL_OTP: 'EMAIL_OTP_CODE',
  SMS_OTP: 'SMS_OTP_CODE',
  SMS_MFA: 'SMS_MFA_CODE',
  SOFTWARE_TOKEN_MFA: 'SOFTWARE_TOKEN_MFA_CODE',
  CUSTOM_CHALLENGE: 'ANSWER',
  SELECT_CHALLENGE: 'ANSWER',
};

@Injectable({ providedIn: 'root' })
export class CognitoClient {
  private readonly config = inject(RUNTIME_CONFIG);

  /**
   * Demande à Cognito quelles méthodes il accepte pour cette adresse.
   *
   * La liste vient du serveur et n'est jamais figée dans le code : un compte
   * ayant activé la double authentification ne peut pas utiliser le code par
   * e-mail en premier facteur, et le pool n'expose ce code qu'une fois SES
   * vérifié. Seul Cognito connaît l'état réel.
   */
  async listSignInOptions(email: string): Promise<AuthOutcome> {
    const response = await this.call<AuthResponse>('InitiateAuth', {
      AuthFlow: 'USER_AUTH',
      ClientId: this.clientId,
      AuthParameters: { USERNAME: email },
    });
    return this.toOutcome(response);
  }

  async signInWithPassword(email: string, password: string): Promise<AuthOutcome> {
    const response = await this.call<AuthResponse>('InitiateAuth', {
      AuthFlow: 'USER_AUTH',
      ClientId: this.clientId,
      AuthParameters: { USERNAME: email, PASSWORD: password, PREFERRED_CHALLENGE: 'PASSWORD' },
    });
    return this.toOutcome(response);
  }

  /** Déclenche l'envoi d'un code à six chiffres par Cognito. */
  async requestEmailCode(email: string): Promise<AuthOutcome> {
    const response = await this.call<AuthResponse>('InitiateAuth', {
      AuthFlow: 'USER_AUTH',
      ClientId: this.clientId,
      AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: 'EMAIL_OTP' },
    });
    return this.toOutcome(response);
  }

  /**
   * Demande l'envoi d'un lien de connexion.
   *
   * La session renvoyée est délibérément ignorée : le lien sera très souvent
   * ouvert ailleurs — sur un téléphone alors que la demande venait d'un
   * ordinateur — et la page d'atterrissage repart d'une session neuve.
   */
  async requestMagicLink(email: string): Promise<void> {
    await this.call<AuthResponse>('InitiateAuth', {
      AuthFlow: 'CUSTOM_AUTH',
      ClientId: this.clientId,
      AuthParameters: { USERNAME: email },
      ClientMetadata: { intent: 'request' },
    });
  }

  /**
   * Échange le jeton du lien contre une session.
   *
   * `intent: 'verify'` indique au déclencheur de ne pas envoyer un second
   * e-mail : sans cela, chaque clic sur un lien en déclencherait un nouveau.
   */
  async redeemMagicLink(email: string, token: string): Promise<AuthOutcome> {
    const started = await this.call<AuthResponse>('InitiateAuth', {
      AuthFlow: 'CUSTOM_AUTH',
      ClientId: this.clientId,
      AuthParameters: { USERNAME: email },
      ClientMetadata: { intent: 'verify' },
    });

    const outcome = this.toOutcome(started);
    if (outcome.kind !== 'challenge') {
      throw new CognitoError('unknown', "Cognito n'a pas présenté le défi attendu.");
    }
    return this.answerChallenge(email, outcome.pending, token);
  }

  async answerChallenge(
    email: string,
    pending: PendingChallenge,
    answer: string,
  ): Promise<AuthOutcome> {
    const key = ANSWER_KEY[pending.challenge];
    if (!key) {
      throw new CognitoError('unknown', `Défi non pris en charge : ${pending.challenge}.`);
    }

    const response = await this.call<AuthResponse>('RespondToAuthChallenge', {
      ClientId: this.clientId,
      ChallengeName: pending.challenge,
      Session: pending.session,
      ChallengeResponses: { USERNAME: email, [key]: answer },
      ClientMetadata: { intent: 'verify' },
    });
    return this.toOutcome(response);
  }

  /** Retenu depuis la liste de choix : sélectionne une méthode et provoque son défi. */
  async selectChallenge(
    email: string,
    session: string,
    challenge: ChallengeName,
  ): Promise<AuthOutcome> {
    return this.answerChallenge(email, { challenge: 'SELECT_CHALLENGE', session }, challenge);
  }

  /** @returns `true` si le compte est déjà confirmé et ne demande aucun code. */
  async signUp(email: string, password: string, fullName: string): Promise<boolean> {
    const response = await this.call<{ UserConfirmed?: boolean }>('SignUp', {
      ClientId: this.clientId,
      Username: email,
      Password: password,
      UserAttributes: [
        { Name: 'email', Value: email },
        { Name: 'name', Value: fullName },
      ],
    });
    return response.UserConfirmed === true;
  }

  async confirmSignUp(email: string, code: string): Promise<void> {
    await this.call('ConfirmSignUp', {
      ClientId: this.clientId,
      Username: email,
      ConfirmationCode: code,
    });
  }

  async resendConfirmationCode(email: string): Promise<void> {
    await this.call('ResendConfirmationCode', { ClientId: this.clientId, Username: email });
  }

  async forgotPassword(email: string): Promise<void> {
    await this.call('ForgotPassword', { ClientId: this.clientId, Username: email });
  }

  async confirmForgotPassword(email: string, code: string, password: string): Promise<void> {
    await this.call('ConfirmForgotPassword', {
      ClientId: this.clientId,
      Username: email,
      ConfirmationCode: code,
      Password: password,
    });
  }

  async refresh(refreshToken: string): Promise<AuthTokens> {
    const response = await this.call<AuthResponse>('InitiateAuth', {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: this.clientId,
      AuthParameters: { REFRESH_TOKEN: refreshToken },
    });
    const outcome = this.toOutcome(response);
    if (outcome.kind !== 'tokens') {
      throw new CognitoError('NotAuthorizedException', 'La session a expiré.');
    }
    return outcome.tokens;
  }

  /**
   * Invalide le jeton de rafraîchissement côté Cognito.
   *
   * Sans cet appel, effacer les jetons du navigateur laisserait une session
   * valide pendant trente jours pour qui aurait pu les recopier.
   */
  async revoke(refreshToken: string): Promise<void> {
    await this.call('RevokeToken', { ClientId: this.clientId, Token: refreshToken });
  }

  private get clientId(): string {
    return this.config.cognito.userPoolClientId;
  }

  private toOutcome(response: AuthResponse): AuthOutcome {
    const result = response.AuthenticationResult;
    if (result?.AccessToken && result.IdToken) {
      return {
        kind: 'tokens',
        tokens: {
          accessToken: result.AccessToken,
          idToken: result.IdToken,
          refreshToken: result.RefreshToken,
          expiresAt: Date.now() + (result.ExpiresIn ?? 3600) * 1000,
        },
      };
    }

    const session = response.Session ?? '';
    if (response.ChallengeName === 'SELECT_CHALLENGE') {
      return {
        kind: 'choice',
        available: (response.AvailableChallenges ?? []) as ChallengeName[],
        session,
      };
    }
    if (response.ChallengeName) {
      return {
        kind: 'challenge',
        pending: { challenge: response.ChallengeName as ChallengeName, session },
      };
    }

    throw new CognitoError('unknown', "Réponse inattendue du service d'authentification.");
  }

  private async call<T>(operation: string, body: Record<string, unknown>): Promise<T> {
    const response = await fetch(`https://cognito-idp.${this.config.region}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
      },
      body: JSON.stringify(body),
      // Aucun cookie n'a de sens ici, et en envoyer exposerait la requête au CSRF.
      credentials: 'omit',
    });

    const payload: unknown = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = payload as { __type?: unknown; message?: unknown };
      throw new CognitoError(
        parseErrorCode(error.__type),
        typeof error.message === 'string' ? error.message : `Erreur HTTP ${response.status}.`,
      );
    }
    return payload as T;
  }
}
